import React, { useState } from "react";
import {
  IconButton,
  Typography,
  Modal,
  Box,
  Button,
  MenuItem,
  Divider,
  Avatar,
  Tooltip,
  Menu,
  ListItemIcon,
  Fade,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useAuth } from "../../context/AuthContext";
import { Logout, Person } from "@mui/icons-material";

export const AvatarHeader = ({ userMe }) => {
  const { logout } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const userName = userMe?.user_name || "Usuario";
  const userRole = userMe?.rol || "Sin rol asignado";
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogoutClick = () => {
    setShowModal(true);
    handleClose();
  };

  return (
    <>
      <Box sx={{ position: "relative" }}>
        <Tooltip
          title="Perfil de usuario"
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: "#1a1a1a",
                color: "#ffffff",
                fontSize: "0.75rem",
                fontWeight: 300,
                letterSpacing: "0.02em",
                borderRadius: 0,
                border: "1px solid #333333",
              },
            },
          }}
        >
          <IconButton
            onClick={handleClick}
            size="small"
            aria-controls={open ? "account-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
            sx={{
              padding: 0,
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                top: -2,
                left: -2,
                right: -2,
                bottom: -2,
                border: "1px solid transparent",
                borderRadius: "50%",
                transition: "border-color 0.3s ease",
              },
              "&:hover::before": {
                borderColor: "#666666",
              },
            }}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                backgroundColor: "#111111",
                border: "1px solid #333333",
                color: "#ffffff",
                fontSize: "0.875rem",
                fontWeight: 300,
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: "#1a1a1a",
                  borderColor: "#555555",
                },
              }}
            >
              {userName.charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>
        </Tooltip>

        <Box
          sx={{
            position: "absolute",
            bottom: 2,
            right: 2,
            width: 8,
            height: 8,
            backgroundColor: "#00ff88",
            borderRadius: "50%",
            border: "1px solid #0a0a0a",
            animation: "pulse 2s infinite",
            "@keyframes pulse": {
              "0%, 100%": { opacity: 0.6 },
              "50%": { opacity: 1 },
            },
          }}
        />
      </Box>

      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        TransitionComponent={Fade}
        PaperProps={{
          sx: {
            backgroundColor: "#111111",
            border: "1px solid #333333",
            borderRadius: 0,
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
            minWidth: 240,
            mt: 1,
            "& .MuiMenuItem-root": {
              color: "#ffffff",
              fontSize: "0.875rem",
              fontWeight: 300,
              py: 1.5,
              px: 2,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "#1a1a1a",
              },
            },
            "& .MuiListItemIcon-root": {
              color: "#666666",
              minWidth: 36,
            },
            "& .MuiDivider-root": {
              borderColor: "#333333",
              my: 1,
            },
          },
        }}
      >
        <MenuItem
          disableRipple
          sx={{
            pointerEvents: "none",
            backgroundColor: "transparent !important",
            py: 2,
            px: 2,
          }}
        >
          <ListItemIcon>
            <Person sx={{ color: "#888888" }} />
          </ListItemIcon>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 400,
                color: "#ffffff",
                fontSize: "0.875rem",
                mb: 0.5,
              }}
            >
              {userName}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.75rem",
                color: "#666666",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                fontWeight: 300,
              }}
            >
              {userRole}
            </Typography>
          </Box>
        </MenuItem>

        <Divider />

        <MenuItem
          onClick={handleLogoutClick}
          sx={{
            "&:hover .MuiListItemIcon-root": {
              color: "#ff6b6b",
            },
          }}
        >
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2" sx={{ fontWeight: 300 }}>
            Cerrar Sesión
          </Typography>
        </MenuItem>
      </Menu>

      <ModalLogout
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        logout={logout}
        userName={userName}
      />
    </>
  );
};

const ModalLogout = ({ isOpen, onClose, logout, userName }) => {
  const handleConfirmLogout = () => {
    logout();
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backdropFilter: "blur(8px)",
        backgroundColor: "rgba(0, 0, 0, 0.8)",
      }}
    >
      <Fade in={isOpen}>
        <Box
          sx={{
            width: { xs: "90vw", sm: 480 },
            backgroundColor: "#fafafa",
            border: "1px solid #e0e0e0",
            borderRadius: 0,
            position: "relative",
            outline: "none",
            boxShadow: "0 24px 48px rgba(0, 0, 0, 0.2)",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 3,
              backgroundColor: "#1a1a1a",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 4,
              pb: 3,
              borderBottom: "1px solid #e8e8e8",
            }}
          >
            <Box>
              <Typography
                variant="h5"
                sx={{
                  color: "#1a1a1a",
                  fontWeight: 500,
                  fontSize: "1.375rem",
                  letterSpacing: "-0.02em",
                  mb: 0.5,
                }}
              >
                Cerrar Sesión
              </Typography>
            </Box>

            <IconButton
              onClick={onClose}
              size="medium"
              sx={{
                color: "#888888",
                backgroundColor: "#f5f5f5",
                borderRadius: 0,
                p: 1.5,
                border: "1px solid #e0e0e0",
                transition: "all 0.2s ease",
                "&:hover": {
                  color: "#1a1a1a",
                  backgroundColor: "#e8e8e8",
                  borderColor: "#d0d0d0",
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ p: 4 }}>
            <Typography
              variant="body1"
              sx={{
                color: "#333333",
                fontSize: "1rem",
                lineHeight: 1.6,
                fontWeight: 400,
                mb: 1,
              }}
            >
              ¿Está seguro que desea finalizar su sesión actual?
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "#666666",
                fontSize: "0.875rem",
                fontWeight: 400,
                mb: 4,
                lineHeight: 1.5,
              }}
            >
              Será redirigido a la página de inicio de sesión y deberá
              autenticarse nuevamente para acceder al sistema.
            </Typography>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{
                  borderColor: "#d0d0d0",
                  color: "#666666",
                  backgroundColor: "transparent",
                  borderRadius: 0,
                  px: 4,
                  py: 1.5,
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  textTransform: "none",
                  minWidth: 100,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: "#999999",
                    backgroundColor: "#f5f5f5",
                    color: "#333333",
                  },
                }}
              >
                Cancelar
              </Button>

              <Button
                variant="contained"
                onClick={handleConfirmLogout}
                sx={{
                  backgroundColor: "#1a1a1a",
                  color: "#ffffff",
                  borderRadius: 0,
                  px: 4,
                  py: 1.5,
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  textTransform: "none",
                  minWidth: 120,
                  boxShadow: "0 2px 8px rgba(26, 26, 26, 0.15)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#333333",
                    boxShadow: "0 4px 16px rgba(26, 26, 26, 0.25)",
                    transform: "translateY(-1px)",
                  },
                }}
              >
                Cerrar Sesión
              </Button>
            </Box>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};
